import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { AccountBundleV1, DeviceBundleRecordV1 } from './types';
import { base64url, canonicalBytes, fromUtf8, unbase64url, utf8 } from './encoding';
import { assertBundle, fingerprint, randomBytes } from './primitives';

interface CryptoDatabase extends DBSchema {
  deviceKeys: { key: string; value: CryptoKey };
  bundles: { key: string; value: DeviceBundleRecordV1 };
  fingerprints: { key: string; value: { fingerprint: string; acceptedAt: string } };
}

export class DeviceKeyStore {
  private database?: IDBPDatabase<CryptoDatabase>;
  constructor(private readonly scope: string) {}
  private async db() {
    this.database ??= await openDB<CryptoDatabase>(`holdthat-crypto-${this.scope.replace(/[^a-zA-Z0-9_-]/g, '_')}`, 1, {
      upgrade(database) {
        database.createObjectStore('deviceKeys'); database.createObjectStore('bundles'); database.createObjectStore('fingerprints');
      },
    });
    return this.database;
  }
  private async deviceKey(userID: string): Promise<CryptoKey> {
    const database = await this.db(); const existing = await database.get('deviceKeys', userID); if (existing) return existing;
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    await database.put('deviceKeys', key, userID); return key;
  }
  async hasBundle(userID: string) { return Boolean(await (await this.db()).get('bundles', userID)); }
  async saveBundle(userID: string, bundle: AccountBundleV1) {
    const key = await this.deviceKey(userID); const nonce = randomBytes(12); const aad = utf8(`holdthat:device-bundle:v1:${userID}:${bundle.keyRevision}`);
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: aad, tagLength: 128 }, key, canonicalBytes(bundle));
    const record: DeviceBundleRecordV1 = { cryptoVersion: 1, userID, keyRevision: bundle.keyRevision, nonce: base64url(nonce), ciphertext: base64url(ciphertext), fingerprint: await fingerprint(bundle.encryptionPublicKey, bundle.signingPublicKey) };
    await (await this.db()).put('bundles', record, userID);
  }
  async loadBundle(userID: string): Promise<AccountBundleV1 | undefined> {
    const database = await this.db(); const record = await database.get('bundles', userID); if (!record) return undefined;
    const key = await database.get('deviceKeys', userID); if (!key) return undefined;
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unbase64url(record.nonce, 12), additionalData: utf8(`holdthat:device-bundle:v1:${userID}:${record.keyRevision}`), tagLength: 128 }, key, unbase64url(record.ciphertext));
    return assertBundle(JSON.parse(fromUtf8(plaintext)) as AccountBundleV1);
  }
  async pinFingerprint(userID: string, fingerprintValue: string, acceptChange = false) {
    const database = await this.db(); const current = await database.get('fingerprints', userID);
    if (current && current.fingerprint !== fingerprintValue && !acceptChange) throw new Error('SECURITY_KEY_CHANGED');
    await database.put('fingerprints', { fingerprint: fingerprintValue, acceptedAt: new Date().toISOString() }, userID);
  }
  async pinnedFingerprint(userID: string) { return (await (await this.db()).get('fingerprints', userID))?.fingerprint; }
  async forgetBundle(userID: string) { const database = await this.db(); await database.delete('bundles', userID); await database.delete('deviceKeys', userID); }
  close() { this.database?.close(); this.database = undefined; }
}
